import React, { useState, useEffect, useCallback } from 'react'
import {
  Users,
  UserPlus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  Phone,
  Mail,
  Briefcase,
  Loader2,
  Plus,
  FileText,
  Download,
  BadgeCheck,
  Banknote,
  ShieldOff,
  ShieldCheck,
  UserX,
  Link,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
} from 'lucide-react'
import { apiFetch, apiFetchBlob, downloadBlob } from '../api'
import { useStore } from '../store'

function cx(...classes) {
  return classes.filter(Boolean).join(' ')
}

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  INACTIVE: 'bg-amber-50 text-amber-700 ring-amber-100',
  TERMINATED: 'bg-rose-50 text-rose-700 ring-rose-100',
}
const STATUS_LABELS = {
  ACTIVE: 'Actif',
  INACTIVE: 'Inactif',
  TERMINATED: 'Parti',
}

const LEAVE_STATUS_COLORS = {
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-100',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-100',
}
const LEAVE_STATUS_LABELS = {
  PENDING: 'En attente',
  APPROVED: 'Approuvé',
  REJECTED: 'Refusé',
}
const LEAVE_TYPE_LABELS = {
  CONGE_PAYE: 'Congé payé',
  RTT: 'RTT',
  MALADIE: 'Maladie',
  SANS_SOLDE: 'Sans solde',
  AUTRE: 'Autre',
}

const MONTH_NAMES = [
  '',
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
]

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })
}

function Pill({ children, colorClass }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
        colorClass
      )}
    >
      {children}
    </span>
  )
}

// ── Formulaire employé ────────────────────────────────────────
function genPassword() {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$'
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

function EmployeeForm({
  initial = {},
  onSave,
  onCancel,
  loading,
  userAccounts = [],
  isNew = false,
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    hire_date: '',
    status: 'ACTIVE',
    annual_leave_days: 25,
    notes: '',
    employee_number: '',
    contract_type: 'CDI',
    qualification: '',
    user_id: '',
    ...initial,
  })

  // Section compte utilisateur (création uniquement)
  const [createAccount, setCreateAccount] = useState(false)
  const [account, setAccount] = useState({
    username: '',
    password: genPassword(),
    roles: ['sales'],
  })
  const [showPassword, setShowPassword] = useState(false)
  const [copied, setCopied] = useState(false)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const setAcc = (k) => (e) => setAccount((a) => ({ ...a, [k]: e.target.value }))

  // Auto-remplir le username depuis prénom.nom
  function autoUsername() {
    const u = `${form.first_name}.${form.last_name}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9._-]/g, '')
    setAccount((a) => ({ ...a, username: u }))
  }

  function copyPassword() {
    navigator.clipboard.writeText(account.password).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleSubmit() {
    const payload = { ...form }
    // Création : embed create_account dans le payload
    if (isNew && createAccount) {
      payload.create_account = {
        username: account.username.trim(),
        password: account.password,
        roles: account.roles,
      }
      delete payload.user_id
    }
    // Édition sans compte lié + onglet "Créer" : passer les infos compte séparément
    if (!isNew && !form.user_id && createAccount) {
      payload._create_account = {
        username: account.username.trim(),
        password: account.password,
        roles: account.roles,
      }
    }
    onSave(payload)
  }

  const ROLES_OPTIONS = [
    { value: 'sales', label: 'Commercial (sales)' },
    { value: 'ops', label: 'Opérations (ops)' },
    { value: 'auditor', label: 'Auditeur (auditor)' },
    { value: 'admin', label: 'Administrateur (admin)' },
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Prénom *</label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.first_name}
            onChange={set('first_name')}
            placeholder="Prénom"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Nom *</label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.last_name}
            onChange={set('last_name')}
            placeholder="Nom de famille"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Poste</label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.position}
            onChange={set('position')}
            placeholder="Ex: Développeur"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Département</label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.department}
            onChange={set('department')}
            placeholder="Ex: Tech"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
          <input
            type="email"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.email}
            onChange={set('email')}
            placeholder="email@exemple.com"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Téléphone</label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.phone}
            onChange={set('phone')}
            placeholder="+225 07 00 00 00 00"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Date d'entrée</label>
          <input
            type="date"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.hire_date ? form.hire_date.toString().slice(0, 10) : ''}
            onChange={set('hire_date')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Statut</label>
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.status}
            onChange={set('status')}
          >
            <option value="ACTIVE">Actif</option>
            <option value="INACTIVE">Inactif</option>
            <option value="TERMINATED">Parti</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Jours congés/an</label>
          <input
            type="number"
            min="0"
            max="365"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.annual_leave_days}
            onChange={set('annual_leave_days')}
          />
        </div>
      </div>
      <div className="border-t border-slate-100 pt-3 mt-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Informations paie
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">N° Matricule</label>
            <input
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={form.employee_number}
              onChange={set('employee_number')}
              placeholder="Ex: EMP-001"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Type de contrat</label>
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={form.contract_type}
              onChange={set('contract_type')}
            >
              <option value="CDI">CDI</option>
              <option value="CDD">CDD</option>
              <option value="Stage">Stage</option>
              <option value="Intérim">Intérim</option>
              <option value="Freelance">Freelance</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Qualification / Catégorie
          </label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.qualification}
            onChange={set('qualification')}
            placeholder="Ex: Technicien supérieur, Cadre A…"
          />
        </div>
      </div>

      {/* ── Section accès plateforme ── */}
      <div className="border-t border-slate-100 pt-3 mt-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Accès à la plateforme
        </p>

        {isNew ? (
          // Création : toggle pour créer un compte en même temps
          <>
            <button
              type="button"
              onClick={() => setCreateAccount((v) => !v)}
              className={cx(
                'flex items-center gap-3 w-full rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors',
                createAccount
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              )}
            >
              <KeyRound className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">Créer un compte utilisateur pour cet employé</span>
              <span
                className={cx(
                  'ml-auto rounded-full px-2 py-0.5 text-xs font-semibold',
                  createAccount ? 'bg-indigo-200 text-indigo-800' : 'bg-slate-100 text-slate-500'
                )}
              >
                {createAccount ? 'Oui' : 'Non'}
              </span>
            </button>

            {createAccount && (
              <div className="mt-3 space-y-3 rounded-xl bg-indigo-50/60 border border-indigo-100 p-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Nom d'utilisateur *
                    </label>
                    <input
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      value={account.username}
                      onChange={setAcc('username')}
                      placeholder="prenom.nom"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={autoUsername}
                    className="mt-5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                  >
                    Auto
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Rôle</label>
                  <select
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    value={account.roles[0]}
                    onChange={(e) => setAccount((a) => ({ ...a, roles: [e.target.value] }))}
                  >
                    {ROLES_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Mot de passe temporaire
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        value={account.password}
                        onChange={setAcc('password')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={copyPassword}
                      className={cx(
                        'flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors',
                        copied
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                      )}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {copied ? 'Copié !' : 'Copier'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccount((a) => ({ ...a, password: genPassword() }))}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
                    >
                      ↻
                    </button>
                  </div>
                  <p className="text-xs text-amber-600 mt-1.5">
                    ⚠ Notez ce mot de passe avant de sauvegarder, il ne sera plus affiché.
                  </p>
                </div>
              </div>
            )}
          </>
        ) : // Édition : deux options selon si un compte est déjà lié ou non
        form.user_id ? (
          // Compte déjà lié → juste permettre de changer
          <>
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={form.user_id || ''}
              onChange={set('user_id')}
            >
              <option value="">— Aucun compte lié —</option>
              {userAccounts.map((u) => (
                <option key={u.id} value={u.id}>
                  @{u.username}
                  {u.full_name ? ` — ${u.full_name}` : ''}
                  {u.is_active === false ? ' (bloqué)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              Changer ou retirer le compte lié à cet employé.
            </p>
          </>
        ) : (
          // Pas de compte lié → onglets Lier / Créer
          <>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-3">
              <button
                type="button"
                onClick={() => setCreateAccount(false)}
                className={cx(
                  'flex-1 py-2 text-xs font-medium transition-colors',
                  !createAccount
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50'
                )}
              >
                Lier un compte existant
              </button>
              <button
                type="button"
                onClick={() => setCreateAccount(true)}
                className={cx(
                  'flex-1 py-2 text-xs font-medium transition-colors',
                  createAccount
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50'
                )}
              >
                Créer un nouveau compte
              </button>
            </div>

            {!createAccount ? (
              // Onglet Lier
              userAccounts.length > 0 ? (
                <>
                  <select
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    value={form.user_id || ''}
                    onChange={set('user_id')}
                  >
                    <option value="">— Aucun compte lié —</option>
                    {userAccounts.map((u) => (
                      <option key={u.id} value={u.id}>
                        @{u.username}
                        {u.full_name ? ` — ${u.full_name}` : ''}
                        {u.is_active === false ? ' (bloqué)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">
                    Associez cet employé à un compte de connexion existant.
                  </p>
                </>
              ) : (
                <p className="text-xs text-slate-400 py-2">
                  Aucun compte utilisateur disponible. Utilisez "Créer un nouveau compte".
                </p>
              )
            ) : (
              // Onglet Créer
              <div className="space-y-3 rounded-xl bg-indigo-50/60 border border-indigo-100 p-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Nom d'utilisateur *
                    </label>
                    <input
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      value={account.username}
                      onChange={setAcc('username')}
                      placeholder="prenom.nom"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={autoUsername}
                    className="mt-5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                  >
                    Auto
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Rôle</label>
                  <select
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    value={account.roles[0]}
                    onChange={(e) => setAccount((a) => ({ ...a, roles: [e.target.value] }))}
                  >
                    {ROLES_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Mot de passe temporaire
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        value={account.password}
                        onChange={setAcc('password')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={copyPassword}
                      className={cx(
                        'flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors',
                        copied
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                      )}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {copied ? 'Copié !' : 'Copier'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccount((a) => ({ ...a, password: genPassword() }))}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
                    >
                      ↻
                    </button>
                  </div>
                  <p className="text-xs text-amber-600 mt-1.5">
                    ⚠ Notez ce mot de passe avant de sauvegarder, il ne sera plus affiché.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
        <textarea
          rows={2}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          value={form.notes}
          onChange={set('notes')}
          placeholder="Notes internes…"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          Annuler
        </button>
        <button
          disabled={loading}
          onClick={handleSubmit}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isNew && createAccount
            ? 'Créer employé + compte'
            : !isNew && !form.user_id && createAccount
              ? 'Enregistrer + créer compte'
              : 'Enregistrer'}
        </button>
      </div>
    </div>
  )
}

// ── Formulaire congé ──────────────────────────────────────────
function LeaveForm({ employees, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    employee_id: '',
    type: 'CONGE_PAYE',
    start_date: '',
    end_date: '',
    days_count: '',
    reason: '',
  })
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Employé *</label>
        <select
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          value={form.employee_id}
          onChange={set('employee_id')}
        >
          <option value="">— Sélectionner —</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.first_name} {e.last_name}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.type}
            onChange={set('type')}
          >
            {Object.entries(LEAVE_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Nb jours *</label>
          <input
            type="number"
            min="0.5"
            step="0.5"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.days_count}
            onChange={set('days_count')}
            placeholder="Ex: 5"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Date début *</label>
          <input
            type="date"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.start_date}
            onChange={set('start_date')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Date fin *</label>
          <input
            type="date"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.end_date}
            onChange={set('end_date')}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Motif</label>
        <textarea
          rows={2}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          value={form.reason}
          onChange={set('reason')}
          placeholder="Motif optionnel…"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          Annuler
        </button>
        <button
          disabled={loading}
          onClick={() => onSave(form)}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Soumettre
        </button>
      </div>
    </div>
  )
}

// ── Formulaire fiche de paie ──────────────────────────────────
function PayslipForm({ employees, initial = {}, onSave, onCancel, loading }) {
  const now = new Date()
  const normalizedBonuses = Array.isArray(initial.bonuses) ? initial.bonuses : []
  const normalizedDeductions = Array.isArray(initial.deductions) ? initial.deductions : []
  const [form, setForm] = useState({
    employee_id: '',
    period_month: now.getMonth() + 1,
    period_year: now.getFullYear(),
    salary_base: '',
    notes: '',
    ...initial,
    bonuses: normalizedBonuses,
    deductions: normalizedDeductions,
  })

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  function addLine(key) {
    setForm((f) => ({ ...f, [key]: [...f[key], { label: '', amount: '' }] }))
  }
  function removeLine(key, i) {
    setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }))
  }
  function setLine(key, i, field, val) {
    setForm((f) => {
      const arr = f[key].map((item, j) => (j === i ? { ...item, [field]: val } : item))
      return { ...f, [key]: arr }
    })
  }

  const gross =
    Number(form.salary_base || 0) + form.bonuses.reduce((s, b) => s + Number(b.amount || 0), 0)
  const net = gross - form.deductions.reduce((s, d) => s + Number(d.amount || 0), 0)

  return (
    <div className="space-y-4">
      {!initial.id && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Employé *</label>
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.employee_id}
            onChange={set('employee_id')}
          >
            <option value="">— Sélectionner —</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Mois *</label>
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.period_month}
            onChange={set('period_month')}
          >
            {MONTH_NAMES.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Année *</label>
          <input
            type="number"
            min="2020"
            max="2100"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            value={form.period_year}
            onChange={set('period_year')}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Salaire de base *</label>
        <input
          type="number"
          min="0"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          value={form.salary_base}
          onChange={set('salary_base')}
          placeholder="0"
        />
      </div>

      {/* Primes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-slate-600">Primes / Avantages</label>
          <button
            type="button"
            onClick={() => addLine('bonuses')}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
          >
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        {form.bonuses.map((b, i) => (
          <div key={i} className="flex gap-2 mb-1.5">
            <input
              className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Libellé"
              value={b.label}
              onChange={(e) => setLine('bonuses', i, 'label', e.target.value)}
            />
            <input
              type="number"
              min="0"
              className="w-28 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Montant"
              value={b.amount}
              onChange={(e) => setLine('bonuses', i, 'amount', e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeLine('bonuses', i)}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Retenues */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-slate-600">Retenues / Déductions</label>
          <button
            type="button"
            onClick={() => addLine('deductions')}
            className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
          >
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        {form.deductions.map((d, i) => (
          <div key={i} className="flex gap-2 mb-1.5">
            <input
              className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Libellé"
              value={d.label}
              onChange={(e) => setLine('deductions', i, 'label', e.target.value)}
            />
            <input
              type="number"
              min="0"
              className="w-28 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Montant"
              value={d.amount}
              onChange={(e) => setLine('deductions', i, 'amount', e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeLine('deductions', i)}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Aperçu totaux */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
        <div className="flex justify-between text-slate-600 mb-1">
          <span>Salaire brut</span>
          <span className="font-semibold">{fmtMoney(gross)}</span>
        </div>
        <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1.5 mt-1">
          <span>Net à payer</span>
          <span className="text-indigo-700">{fmtMoney(net)}</span>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
        <textarea
          rows={2}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          value={form.notes}
          onChange={set('notes')}
          placeholder="Notes optionnelles…"
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          Annuler
        </button>
        <button
          disabled={loading}
          onClick={() => onSave(form)}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Enregistrer
        </button>
      </div>
    </div>
  )
}

// ── Drawer générique ──────────────────────────────────────────
function Drawer({ open, title, onClose, children }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  )
}

// ── Composant principal ───────────────────────────────────────
export default function Employees() {
  const user = useStore((s) => s.user)
  const roles = Array.isArray(user?.roles) ? user.roles : []
  const isManager = roles.includes('ops') || roles.includes('admin') || roles.includes('superadmin')

  const [tab, setTab] = useState('annuaire')
  const [employees, setEmployees] = useState([])
  const [leaves, setLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [leavesLoading, setLeavesLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('')

  const [payslips, setPayslips] = useState([])
  const [payslipsLoading, setPayslipsLoading] = useState(false)
  const [payslipEmpFilter, setPayslipEmpFilter] = useState('')
  const [payslipYearFilter, setPayslipYearFilter] = useState('')

  const [userAccounts, setUserAccounts] = useState([])

  const [drawer, setDrawer] = useState(null) // 'create-emp' | 'edit-emp' | 'create-leave' | 'create-payslip' | 'edit-payslip'
  const [editTarget, setEditTarget] = useState(null)

  const [confirmDelete, setConfirmDelete] = useState(null)
  const [rejectModal, setRejectModal] = useState(null)
  const [rejectReason, setRejectReason] = useState('')

  const loadEmployees = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiFetch('/api/employees')
      setEmployees(data)
      setError(null)
    } catch (err) {
      setError(err.message || 'Erreur chargement employés')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadLeaves = useCallback(async () => {
    setLeavesLoading(true)
    try {
      const data = await apiFetch('/api/employees/leaves/list')
      setLeaves(data)
    } catch {
      // non bloquant
    } finally {
      setLeavesLoading(false)
    }
  }, [])

  const loadPayslips = useCallback(async () => {
    setPayslipsLoading(true)
    try {
      const params = new URLSearchParams()
      if (payslipEmpFilter) params.set('employee_id', payslipEmpFilter)
      if (payslipYearFilter) params.set('year', payslipYearFilter)
      const qs = params.toString()
      const data = await apiFetch(`/api/employees/payslips/list${qs ? '?' + qs : ''}`)
      setPayslips(data)
    } catch {
      // non bloquant
    } finally {
      setPayslipsLoading(false)
    }
  }, [payslipEmpFilter, payslipYearFilter])

  useEffect(() => {
    loadEmployees()
  }, [loadEmployees])
  useEffect(() => {
    if (tab === 'conges') loadLeaves()
  }, [tab, loadLeaves])
  useEffect(() => {
    if (tab === 'paies') loadPayslips()
  }, [tab, loadPayslips])
  useEffect(() => {
    if (!isManager) return
    apiFetch('/api/admin/users')
      .then(setUserAccounts)
      .catch(() => {})
  }, [isManager])

  // ── Filtres annuaire ──
  const filteredEmployees = employees.filter((e) => {
    const q = search.toLowerCase()
    const matchSearch =
      !q ||
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.position || '').toLowerCase().includes(q) ||
      (e.department || '').toLowerCase().includes(q)
    const matchStatus = !statusFilter || e.status === statusFilter
    return matchSearch && matchStatus
  })

  const filteredLeaves = leaves.filter((l) => !leaveStatusFilter || l.status === leaveStatusFilter)

  // ── Save employé ──
  async function handleSaveEmployee(form) {
    if (!form.first_name || !form.last_name) return
    setSaving(true)
    try {
      if (editTarget) {
        const { _create_account, ...empData } = form
        // D'abord sauvegarder les données employé
        await apiFetch(`/api/employees/${editTarget.id}`, { method: 'PATCH', body: empData })
        // Ensuite créer le compte si demandé
        if (_create_account && _create_account.username) {
          await apiFetch(`/api/employees/${editTarget.id}/account`, {
            method: 'POST',
            body: _create_account,
          })
        }
      } else {
        await apiFetch('/api/employees', { method: 'POST', body: form })
      }
      setDrawer(null)
      setEditTarget(null)
      await loadEmployees()
    } catch (err) {
      alert(err.message || 'Erreur enregistrement')
    } finally {
      setSaving(false)
    }
  }

  // ── Delete employé ──
  async function handleDelete(emp) {
    try {
      await apiFetch(`/api/employees/${emp.id}`, { method: 'DELETE' })
      setConfirmDelete(null)
      await loadEmployees()
    } catch (err) {
      alert(err.message || 'Erreur suppression')
    }
  }

  // ── Toggle compte utilisateur lié (bloquer / débloquer) ──
  async function handleToggleUserAccount(emp) {
    if (!emp.user_id) return
    const action = emp.user_is_active !== false ? 'bloquer' : 'débloquer'
    if (
      !confirm(
        `${action.charAt(0).toUpperCase() + action.slice(1)} le compte de @${emp.linked_username} ?`
      )
    )
      return
    try {
      const res = await apiFetch(`/api/admin/users/${emp.user_id}/toggle`, { method: 'PATCH' })
      await loadEmployees()
    } catch (err) {
      alert(err.message || 'Erreur modification compte')
    }
  }

  // ── Supprimer compte utilisateur lié ──
  async function handleDeleteUserAccount(emp) {
    if (!emp.user_id) return
    if (
      !confirm(
        `Supprimer définitivement le compte utilisateur @${emp.linked_username} ?\nL'employé restera dans la liste RH mais ne pourra plus se connecter.`
      )
    )
      return
    try {
      await apiFetch(`/api/admin/users/${emp.user_id}`, { method: 'DELETE' })
      await loadEmployees()
    } catch (err) {
      alert(err.message || 'Erreur suppression compte')
    }
  }

  // ── Save congé ──
  async function handleSaveLeave(form) {
    if (!form.employee_id || !form.start_date || !form.end_date || !form.days_count) {
      alert('Veuillez remplir tous les champs obligatoires')
      return
    }
    setSaving(true)
    try {
      await apiFetch('/api/employees/leaves', { method: 'POST', body: form })
      setDrawer(null)
      await loadLeaves()
    } catch (err) {
      alert(err.message || 'Erreur envoi demande')
    } finally {
      setSaving(false)
    }
  }

  // ── Approve congé ──
  async function handleApprove(leave) {
    try {
      await apiFetch(`/api/employees/leaves/${leave.id}/approve`, { method: 'PATCH' })
      await loadLeaves()
    } catch (err) {
      alert(err.message || 'Erreur approbation')
    }
  }

  // ── Reject congé ──
  async function handleReject() {
    if (!rejectModal) return
    try {
      await apiFetch(`/api/employees/leaves/${rejectModal.id}/reject`, {
        method: 'PATCH',
        body: { reason: rejectReason },
      })
      setRejectModal(null)
      setRejectReason('')
      await loadLeaves()
    } catch (err) {
      alert(err.message || 'Erreur rejet')
    }
  }

  // ── Save fiche de paie ──
  async function handleSavePayslip(form) {
    if (!form.employee_id && !editTarget) {
      alert('Veuillez sélectionner un employé')
      return
    }
    if (!form.salary_base) {
      alert('Salaire de base requis')
      return
    }
    setSaving(true)
    try {
      if (editTarget) {
        await apiFetch(`/api/employees/payslips/${editTarget.id}`, { method: 'PATCH', body: form })
      } else {
        await apiFetch('/api/employees/payslips', { method: 'POST', body: form })
      }
      setDrawer(null)
      setEditTarget(null)
      await loadPayslips()
    } catch (err) {
      alert(err.message || 'Erreur enregistrement fiche de paie')
    } finally {
      setSaving(false)
    }
  }

  // ── Valider fiche de paie ──
  async function handleValidatePayslip(ps) {
    if (
      !window.confirm(
        `Valider la fiche de ${ps.employee_name} — ${MONTH_NAMES[ps.period_month]} ${ps.period_year} ?`
      )
    )
      return
    try {
      await apiFetch(`/api/employees/payslips/${ps.id}/validate`, { method: 'POST' })
      await loadPayslips()
    } catch (err) {
      alert(err.message || 'Erreur validation')
    }
  }

  // ── Supprimer fiche de paie (DRAFT only) ──
  async function handleDeletePayslip(ps) {
    if (!window.confirm(`Supprimer la fiche brouillon de ${ps.employee_name} ?`)) return
    try {
      await apiFetch(`/api/employees/payslips/${ps.id}`, { method: 'DELETE' })
      await loadPayslips()
    } catch (err) {
      alert(err.message || 'Erreur suppression')
    }
  }

  // ── Télécharger PDF ──
  async function handleDownloadPayslipPdf(ps) {
    try {
      const blob = await apiFetchBlob(`/api/employees/payslips/${ps.id}/pdf`)
      const fname = `bulletin-paie-${(ps.employee_name || 'employe').replace(/\s+/g, '-').toLowerCase()}-${MONTH_NAMES[ps.period_month]}-${ps.period_year}.pdf`
      downloadBlob(blob, fname)
    } catch (err) {
      alert(err.message || 'Erreur génération PDF')
    }
  }

  const activeCount = employees.filter((e) => e.status === 'ACTIVE').length
  const pendingLeavesCount = leaves.filter((l) => l.status === 'PENDING').length

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-600" />
            Ressources Humaines
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {activeCount} employé{activeCount > 1 ? 's' : ''} actif{activeCount > 1 ? 's' : ''}
            {pendingLeavesCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                {pendingLeavesCount} congé{pendingLeavesCount > 1 ? 's' : ''} en attente
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {tab === 'annuaire' && isManager && (
            <button
              onClick={() => {
                setEditTarget(null)
                setDrawer('create-emp')
              }}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <UserPlus className="h-4 w-4" />
              Ajouter employé
            </button>
          )}
          {tab === 'conges' && (
            <button
              onClick={() => setDrawer('create-leave')}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4" />
              Nouvelle demande
            </button>
          )}
          {tab === 'paies' && isManager && (
            <button
              onClick={() => {
                setEditTarget(null)
                setDrawer('create-payslip')
              }}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <Banknote className="h-4 w-4" />
              Nouvelle fiche
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="mb-5 flex gap-1 rounded-xl bg-white border border-slate-200 p-1 w-fit shadow-sm">
        {[
          { key: 'annuaire', label: 'Annuaire', icon: Users },
          { key: 'conges', label: 'Congés', icon: Calendar },
          { key: 'paies', label: 'Fiches de paie', icon: Banknote },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cx(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Annuaire ── */}
      {tab === 'annuaire' && (
        <>
          {/* Filtres */}
          <div className="mb-4 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un employé…"
                className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Tous les statuts</option>
              <option value="ACTIVE">Actif</option>
              <option value="INACTIVE">Inactif</option>
              <option value="TERMINATED">Parti</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm">
              {error}
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
              <Users className="mb-3 h-10 w-10 opacity-30" />
              <p className="font-medium">Aucun employé trouvé</p>
              {isManager && (
                <button
                  onClick={() => {
                    setEditTarget(null)
                    setDrawer('create-emp')
                  }}
                  className="mt-4 flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  <UserPlus className="h-4 w-4" /> Ajouter le premier employé
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredEmployees.map((emp) => (
                <div
                  key={emp.id}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm">
                        {emp.first_name[0]}
                        {emp.last_name[0]}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800 text-sm">
                          {emp.first_name} {emp.last_name}
                        </div>
                        {emp.position && (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <Briefcase className="h-3 w-3" />
                            {emp.position}
                          </div>
                        )}
                      </div>
                    </div>
                    <Pill colorClass={STATUS_COLORS[emp.status] || STATUS_COLORS.ACTIVE}>
                      {STATUS_LABELS[emp.status] || emp.status}
                    </Pill>
                  </div>

                  <div className="space-y-1.5 text-xs text-slate-500">
                    {emp.department && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 shrink-0" />
                        <span>{emp.department}</span>
                      </div>
                    )}
                    {emp.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{emp.email}</span>
                      </div>
                    )}
                    {emp.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span>{emp.phone}</span>
                      </div>
                    )}
                    {emp.hire_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          Depuis{' '}
                          {new Date(emp.hire_date).toLocaleDateString('fr-FR', {
                            month: 'long',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                    {emp.contract_type && (
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600 font-medium text-xs">
                          {emp.contract_type}
                        </span>
                        {emp.employee_number && (
                          <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-600 font-medium text-xs">
                            #{emp.employee_number}
                          </span>
                        )}
                      </div>
                    )}
                    {emp.user_id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span
                          className={cx(
                            'rounded px-1.5 py-0.5 text-xs font-semibold ring-1',
                            emp.user_is_active !== false
                              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                              : 'bg-rose-50 text-rose-700 ring-rose-200'
                          )}
                        >
                          @{emp.linked_username}
                        </span>
                        <span
                          className={cx(
                            'text-xs',
                            emp.user_is_active !== false
                              ? 'text-emerald-600'
                              : 'text-rose-500 font-medium'
                          )}
                        >
                          {emp.user_is_active !== false ? 'Accès actif' : 'Compte bloqué'}
                        </span>
                        {isManager && (
                          <div className="flex gap-1 ml-auto">
                            <button
                              onClick={() => handleToggleUserAccount(emp)}
                              title={
                                emp.user_is_active !== false
                                  ? 'Bloquer le compte'
                                  : 'Débloquer le compte'
                              }
                              className={cx(
                                'rounded p-1 transition-colors',
                                emp.user_is_active !== false
                                  ? 'text-amber-500 hover:bg-amber-50 hover:text-amber-700'
                                  : 'text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700'
                              )}
                            >
                              {emp.user_is_active !== false ? (
                                <ShieldOff className="h-3.5 w-3.5" />
                              ) : (
                                <ShieldCheck className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteUserAccount(emp)}
                              title="Supprimer le compte utilisateur"
                              className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                            >
                              <UserX className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      isManager && (
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Link className="h-3 w-3" />
                          <span>Aucun compte lié</span>
                        </div>
                      )
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">{emp.annual_leave_days} j/an</span>
                    {isManager && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditTarget(emp)
                            setDrawer('edit-emp')
                          }}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-indigo-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(emp)}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Congés ── */}
      {tab === 'conges' && (
        <>
          <div className="mb-4 flex gap-3">
            <select
              value={leaveStatusFilter}
              onChange={(e) => setLeaveStatusFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Tous les statuts</option>
              <option value="PENDING">En attente</option>
              <option value="APPROVED">Approuvé</option>
              <option value="REJECTED">Refusé</option>
            </select>
          </div>

          {leavesLoading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
              <Calendar className="mb-3 h-10 w-10 opacity-30" />
              <p className="font-medium">Aucune demande de congé</p>
              <button
                onClick={() => setDrawer('create-leave')}
                className="mt-4 flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" /> Soumettre une demande
              </button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Employé
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Période
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Jours
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Statut
                    </th>
                    {isManager && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeaves.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-800">{l.employee_name}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {LEAVE_TYPE_LABELS[l.type] || l.type}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {new Date(l.start_date).toLocaleDateString('fr-FR')}
                        {' → '}
                        {new Date(l.end_date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-slate-700">
                        {l.days_count}
                      </td>
                      <td className="px-4 py-3">
                        <Pill
                          colorClass={LEAVE_STATUS_COLORS[l.status] || LEAVE_STATUS_COLORS.PENDING}
                        >
                          {LEAVE_STATUS_LABELS[l.status] || l.status}
                        </Pill>
                        {l.rejection_reason && (
                          <div className="mt-1 text-xs text-rose-500">{l.rejection_reason}</div>
                        )}
                      </td>
                      {isManager && (
                        <td className="px-4 py-3 text-right">
                          {l.status === 'PENDING' && (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleApprove(l)}
                                className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                              >
                                <CheckCircle className="h-3.5 w-3.5" /> Approuver
                              </button>
                              <button
                                onClick={() => {
                                  setRejectModal(l)
                                  setRejectReason('')
                                }}
                                className="flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
                              >
                                <XCircle className="h-3.5 w-3.5" /> Refuser
                              </button>
                            </div>
                          )}
                          {l.status !== 'PENDING' && l.approved_by_username && (
                            <span className="text-xs text-slate-400">
                              par @{l.approved_by_username}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Fiches de paie ── */}
      {tab === 'paies' && (
        <>
          <div className="mb-4 flex flex-wrap gap-3">
            <select
              value={payslipEmpFilter}
              onChange={(e) => setPayslipEmpFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Tous les employés</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.first_name} {e.last_name}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="2020"
              max="2100"
              value={payslipYearFilter}
              onChange={(e) => setPayslipYearFilter(e.target.value)}
              placeholder="Année…"
              className="w-28 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {payslipsLoading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : payslips.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
              <Banknote className="mb-3 h-10 w-10 opacity-30" />
              <p className="font-medium">Aucune fiche de paie</p>
              {isManager && (
                <button
                  onClick={() => {
                    setEditTarget(null)
                    setDrawer('create-payslip')
                  }}
                  className="mt-4 flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4" /> Créer une fiche
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Employé
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Période
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Brut
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Net
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payslips.map((ps) => (
                    <tr key={ps.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{ps.employee_name}</div>
                        {ps.position && <div className="text-xs text-slate-400">{ps.position}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {MONTH_NAMES[ps.period_month]} {ps.period_year}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-700">
                        {fmtMoney(ps.gross_salary)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-indigo-700">
                        {fmtMoney(ps.net_salary)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {ps.status === 'VALIDATED' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                            <BadgeCheck className="h-3.5 w-3.5" /> Validée
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-100">
                            Brouillon
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleDownloadPayslipPdf(ps)}
                            title="Télécharger PDF"
                            className="flex items-center gap-1 rounded-lg bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                          >
                            <Download className="h-3.5 w-3.5" /> PDF
                          </button>
                          {isManager && ps.status === 'DRAFT' && (
                            <>
                              <button
                                onClick={() => {
                                  setEditTarget(ps)
                                  setDrawer('edit-payslip')
                                }}
                                title="Modifier"
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-indigo-600"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              {roles.includes('admin') || roles.includes('superadmin') ? (
                                <button
                                  onClick={() => handleValidatePayslip(ps)}
                                  title="Valider"
                                  className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                                >
                                  <BadgeCheck className="h-3.5 w-3.5" /> Valider
                                </button>
                              ) : null}
                              <button
                                onClick={() => handleDeletePayslip(ps)}
                                title="Supprimer"
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Drawer: Créer/Éditer employé */}
      <Drawer
        open={drawer === 'create-emp' || drawer === 'edit-emp'}
        title={drawer === 'edit-emp' ? "Modifier l'employé" : 'Nouvel employé'}
        onClose={() => {
          setDrawer(null)
          setEditTarget(null)
        }}
      >
        <EmployeeForm
          initial={editTarget || {}}
          onSave={handleSaveEmployee}
          onCancel={() => {
            setDrawer(null)
            setEditTarget(null)
          }}
          loading={saving}
          userAccounts={userAccounts}
          isNew={drawer === 'create-emp'}
        />
      </Drawer>

      {/* Drawer: Nouvelle demande de congé */}
      <Drawer
        open={drawer === 'create-leave'}
        title="Nouvelle demande de congé"
        onClose={() => setDrawer(null)}
      >
        <LeaveForm
          employees={employees.filter((e) => e.status === 'ACTIVE')}
          onSave={handleSaveLeave}
          onCancel={() => setDrawer(null)}
          loading={saving}
        />
      </Drawer>

      {/* Drawer: Fiche de paie */}
      <Drawer
        open={drawer === 'create-payslip' || drawer === 'edit-payslip'}
        title={drawer === 'edit-payslip' ? 'Modifier la fiche de paie' : 'Nouvelle fiche de paie'}
        onClose={() => {
          setDrawer(null)
          setEditTarget(null)
        }}
      >
        <PayslipForm
          employees={employees.filter((e) => e.status === 'ACTIVE')}
          initial={editTarget || {}}
          onSave={handleSavePayslip}
          onCancel={() => {
            setDrawer(null)
            setEditTarget(null)
          }}
          loading={saving}
        />
      </Drawer>

      {/* Modal: Confirmer suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setConfirmDelete(null)}
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-rose-500" />
              <h3 className="font-semibold text-slate-800">Supprimer l'employé ?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              {confirmDelete.first_name} {confirmDelete.last_name} sera définitivement supprimé
              ainsi que ses demandes de congé.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Motif de rejet */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setRejectModal(null)}
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="font-semibold text-slate-800 mb-3">Motif du refus</h3>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 mb-4"
              placeholder="Motif optionnel…"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRejectModal(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
